/**
 * AddMapLayersDialog — the unified "Add layers" entry point on the Site Map
 * tab. One dropzone accepts KMZ / KML / GeoJSON / zipped-or-loose shapefiles
 * / DWG; the import router plans + executes each format through its parser,
 * every result funnels into ONE editable review step (rename / recategorize /
 * per-row selection), and commit flows through the existing merge + managed-
 * layer guards into `store.updateSiteLayers`.
 *
 * Site-parcel candidates (polygons the classifier scores as boundaries) are
 * imported as OVERLAY layers here — replacing the site boundary stays with
 * the proven SiteLocationBoundaryEditor import flow (elevation cascade,
 * parcel aggregate), reachable via the banner's "Edit boundary" route.
 *
 * Provenance: the original files are archived best-effort under
 * users/{uid}/sites/{siteId}/layer-imports/{importId}/ and committed layers
 * carry `attributes.importId` / `importFileName`; a failed archive never
 * blocks the commit.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, FileUp, Landmark, Loader2, UploadCloud, X } from 'lucide-react';
import { toast } from 'sonner';

import { useStore } from '../../store';
import type { SiteEntity, SiteLayer } from '../../types/site';
import { Button } from '../ui/button';
import { LayerReviewPanel, parsedToSiteLayer } from './LayerReviewPanel';
import { mergeImportedLayers } from '../../services/kmzParser';
import { mergeCadImportedLayers } from '../../lib/cad/cadImport';
import { preserveManagedGenerationImpactLayers } from '../../lib/siteLayers/managedGenerationImpactLayers';
import { GEO_IMPORT_ACCEPT } from '../../services/geoImport/importRouter';
import { uploadLayerImportProvenance } from '../../services/geoImport/layerImportProvenance';
import { materializeSiteBoundaries } from '../../lib/siteBoundaryGeometry';
import { useGeoImportPipeline } from './useGeoImportPipeline';

export interface AddMapLayersDialogProps {
  site: SiteEntity;
  open: boolean;
  onClose: () => void;
  /** Continue successful imports into explicit polygon/role review. */
  onReviewStudyArea?: () => void;
  /** Files handed in by drag-drop onto the map frame (skips the dropzone). */
  initialFiles?: File[] | null;
  /** Route into Edit boundary → Import when the user wants candidates as parcels. */
  onOpenBoundaryImport?: () => void;
  /** Render the live map preview in the review step; tests pass false. */
  showMap?: boolean;
}

export function AddMapLayersDialog({
  site,
  open,
  onClose,
  onReviewStudyArea,
  initialFiles,
  onOpenBoundaryImport,
  showMap = true,
}: AddMapLayersDialogProps) {
  const updateSiteLayers = useStore((s) => s.updateSiteLayers);

  const {
    progress,
    rejections,
    staged,
    parsing,
    processFiles,
    reset: resetPipeline,
  } = useGeoImportPipeline(site.location ?? null);
  const [committing, setCommitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const consumedInitialFiles = useRef(false);

  const reset = useCallback(() => {
    resetPipeline();
    setCommitting(false);
    setDragOver(false);
    consumedInitialFiles.current = false;
  }, [resetPipeline]);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  // Drag-drop hand-off from the map frame (Phase-2 entry point).
  useEffect(() => {
    if (open && initialFiles && initialFiles.length > 0 && !consumedInitialFiles.current) {
      consumedInitialFiles.current = true;
      void processFiles(initialFiles);
    }
  }, [open, initialFiles, processFiles]);

  const handleCommit = useCallback(async (selected: SiteLayer[]) => {
    if (selected.length === 0) return;
    setCommitting(true);
    try {
      const existing = site.siteLayers ?? [];
      const cadSelected = selected.filter((l) => l.source === 'cad-import');
      const normalSelected = selected.filter((l) => l.source !== 'cad-import');

      const normalMerge = mergeImportedLayers(existing, normalSelected);
      const cadMerge = mergeCadImportedLayers(normalMerge.merged, cadSelected);
      const finalLayers = preserveManagedGenerationImpactLayers(existing, cadMerge.merged);

      await updateSiteLayers(site.id, finalLayers);

      const added = normalMerge.added.length + cadMerge.added.length;
      const skipped = normalMerge.skipped.length + cadMerge.alreadyPresent.length;
      toast.success(
        `${added} layer${added === 1 ? '' : 's'} added to the site map` +
        (skipped > 0 ? ` — ${skipped} duplicate${skipped === 1 ? '' : 's'} skipped` : ''),
      );

      // Best-effort provenance archive; never blocks or fails the commit.
      for (const { importId, files } of staged?.provenance ?? []) {
        for (const file of files) {
          void uploadLayerImportProvenance(site.id, file, importId);
        }
      }

      onClose();
      onReviewStudyArea?.();
    } catch (e) {
      toast.error(`Saving layers failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setCommitting(false);
    }
  }, [site.id, site.siteLayers, staged, updateSiteLayers, onClose, onReviewStudyArea]);

  if (!open) return null;

  const boundary = materializeSiteBoundaries(site)[0]?.polygon ?? [];
  const hasResults = staged !== null;
  const nothingParsed = hasResults && staged.rows.length === 0;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
      data-testid="add-map-layers-dialog"
    >
      <div className="w-full max-w-3xl max-h-[85vh] overflow-y-auto border border-border-subtle bg-surface p-5 space-y-4 rounded-lg">
        <div className="flex items-center gap-2">
          <UploadCloud className="w-4 h-4 text-text-secondary" aria-hidden />
          <h3 className="flex-1 text-xs font-bold uppercase tracking-[0.2em] text-text-primary">
            Add map layers
          </h3>
          <button
            type="button"
            aria-label="Close"
            className="text-text-muted hover:text-text-primary disabled:opacity-40"
            disabled={committing}
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {!hasResults && (
          <div
            data-testid="geo-import-dropzone"
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragOver ? 'border-primary bg-state-hover' : 'border-border-subtle'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              void processFiles(Array.from(e.dataTransfer.files));
            }}
          >
            <FileUp className="w-6 h-6 mx-auto mb-2 text-text-muted" aria-hidden />
            <p className="type-section text-text-primary">
              Drop KMZ, KML, GeoJSON, shapefile (.zip or .shp + sidecars) or DWG files here
            </p>
            <p className="type-meta text-text-muted mt-1">
              Multiple files import together — loose .shp/.dbf/.prj siblings are grouped automatically.
            </p>
            <Button
              variant="outline"
              className="mt-4 h-9 rounded-md type-meta"
              disabled={parsing}
              onClick={() => fileInputRef.current?.click()}
              data-testid="geo-import-browse"
            >
              {parsing ? <Loader2 className="w-3 h-3 mr-2 animate-spin" aria-hidden /> : null}
              Browse files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={GEO_IMPORT_ACCEPT}
              className="hidden"
              data-testid="geo-import-input"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = '';
                void processFiles(files);
              }}
            />
          </div>
        )}

        {rejections.length > 0 && (
          <div className="border border-border-subtle bg-state-hover p-3 space-y-1" data-testid="geo-import-rejections">
            {rejections.map((r, i) => (
              <div key={i} className="flex items-start gap-2 type-meta text-text-secondary">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" aria-hidden />
                <span>{r}</span>
              </div>
            ))}
          </div>
        )}

        {progress.length > 0 && (parsing || !hasResults) && (
          <div className="space-y-1" data-testid="geo-import-progress">
            {progress.map((p, i) => (
              <div key={i} className="flex items-center gap-2 type-meta text-text-secondary">
                {p.status === 'parsing' && <Loader2 className="w-3 h-3 animate-spin" aria-hidden />}
                {p.status === 'done' && <CheckCircle2 className="w-3 h-3 text-emerald-500" aria-hidden />}
                {p.status === 'error' && <AlertCircle className="w-3 h-3 text-red-500" aria-hidden />}
                {p.status === 'pending' && <span className="w-3 h-3 inline-block" aria-hidden />}
                <span className="flex-1 truncate">{p.plan.label}</span>
                {p.error && <span className="text-red-500 truncate max-w-[50%]">{p.error}</span>}
              </div>
            ))}
          </div>
        )}

        {hasResults && staged.warnings.length > 0 && (
          <div className="border border-border-subtle bg-state-hover p-3 space-y-1" data-testid="geo-import-warnings">
            {staged.warnings.map((w, i) => (
              <div key={i} className="flex items-start gap-2 type-meta text-text-secondary">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" aria-hidden />
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}

        {hasResults && staged.boundaryCandidateCount > 0 && (
          <div className="flex items-start gap-2 border border-border-subtle bg-state-hover p-3" data-testid="geo-import-boundary-banner">
            <Landmark className="w-3.5 h-3.5 mt-0.5 shrink-0 text-text-secondary" aria-hidden />
            <p className="flex-1 type-meta text-text-secondary">
              {staged.boundaryCandidateCount} site-parcel candidate{staged.boundaryCandidateCount === 1 ? '' : 's'} detected —
              importing below adds {staged.boundaryCandidateCount === 1 ? 'it' : 'them'} as overlay layers.
              To replace the site boundary, use the boundary editor's import instead.
            </p>
            {onOpenBoundaryImport && (
              <Button
                variant="outline"
                className="h-7 rounded-md type-meta shrink-0"
                onClick={() => { onClose(); onOpenBoundaryImport(); }}
                data-testid="geo-import-open-boundary"
              >
                Edit boundary
              </Button>
            )}
          </div>
        )}

        {nothingParsed && (
          <div className="text-center py-6 space-y-2" data-testid="geo-import-empty">
            <p className="type-section text-text-primary">No importable geometry found.</p>
            <Button variant="outline" className="h-9 rounded-md type-meta" onClick={reset}>
              Try other files
            </Button>
          </div>
        )}

        {hasResults && staged.rows.length > 0 && (
          <LayerReviewPanel
            parsedLayers={staged.rows}
            boundary={boundary}
            showMap={showMap}
            editable
            toSiteLayer={(layer, index) => {
              const prebuilt = staged.cadByRow.get(index);
              if (prebuilt) {
                return {
                  ...prebuilt,
                  name: layer.name,
                  category: layer.category,
                  attributes: { ...prebuilt.attributes, ...layer.attributes },
                };
              }
              return parsedToSiteLayer(layer);
            }}
            onImport={(selected) => { void handleCommit(selected); }}
            onSkip={reset}
          />
        )}

        {committing && (
          <div className="flex items-center gap-2 type-meta text-text-secondary" data-testid="geo-import-committing">
            <Loader2 className="w-3 h-3 animate-spin" aria-hidden />
            Saving layers…
          </div>
        )}
      </div>
    </div>
  );
}
